import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { OrganizationType, ORGANIZATION_TYPE_LABELS } from '../../../types/organization.type';
import { OrganizationOnboardingFormState } from '../../../types/organization-onboarding.type';
import { SAUDI_CITIES } from '../../../constants/saudiCities';

export interface OnboardingStepProps {
  form: OrganizationOnboardingFormState;
  errors: Record<string, string>;
  setField: <K extends keyof OrganizationOnboardingFormState>(
    key: K,
    value: OrganizationOnboardingFormState[K]
  ) => void;
}

const ORG_TYPE_OPTIONS = Object.values(OrganizationType);

export function OnboardingStep1Basics({ form, errors, setField }: OnboardingStepProps) {
  const { t } = useTranslation();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label={t('onboarding.step1.organizationName')}
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
          error={Boolean(errors.name)}
          helperText={errors.name}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth required error={Boolean(errors.organization_type)}>
          <InputLabel id="org-type-label">{t('onboarding.step1.organizationType')}</InputLabel>
          <Select
            labelId="org-type-label"
            label={t('onboarding.step1.organizationType')}
            value={form.organization_type}
            onChange={(e) =>
              setField('organization_type', (e.target.value || '') as OrganizationType | '')
            }
          >
            <MenuItem value="">
              <em>{t('onboarding.step1.selectType')}</em>
            </MenuItem>
            {ORG_TYPE_OPTIONS.map((type) => (
              <MenuItem key={type} value={type}>
                {ORGANIZATION_TYPE_LABELS[type]}
              </MenuItem>
            ))}
          </Select>
          {errors.organization_type ? (
            <FormHelperText>{errors.organization_type}</FormHelperText>
          ) : null}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth required error={Boolean(errors.city)}>
          <InputLabel id="city-label">{t('common.city')}</InputLabel>
          <Select
            labelId="city-label"
            label={t('common.city')}
            value={form.city || ''}
            onChange={(e) => setField('city', e.target.value)}
          >
            <MenuItem value="">
              <em>{t('onboarding.step1.selectCity')}</em>
            </MenuItem>
            {SAUDI_CITIES.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
          {errors.city ? <FormHelperText>{errors.city}</FormHelperText> : null}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label={t('onboarding.step1.phone')}
          placeholder={t('auth.phoneHint')}
          value={form.phone}
          onChange={(e) => setField('phone', e.target.value)}
          error={Boolean(errors.phone)}
          helperText={errors.phone}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label={t('onboarding.step1.contactEmail')}
          type="email"
          value={form.contact_email}
          onChange={(e) => setField('contact_email', e.target.value)}
          error={Boolean(errors.contact_email)}
          helperText={errors.contact_email}
        />
      </Grid>
    </Grid>
  );
}
